import {
  buildSectionDuplicateUpdate,
  getSectionSubTaskIds,
  pairDuplicatedSectionTasks,
  updateLinkedLifeArea,
  updateLinkedRelationships,
} from '@griply/services';
import { useCallback } from 'react';
import { useDuplicateTasksMutation } from 'shared/hooks/queries/use-duplicate-tasks-mutation';
import { useRelationships } from 'shared/hooks/use-relationships';
import {
  getLifeAreaDocById,
  updateLifeArea,
  updateRelationshipReferences,
} from 'shared/services/backend-api';
import { getTaskDocs } from 'shared/services/backend-api/get-tasks';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { chunkArray } from 'shared/utils/chunk-array';
import { v4 as uuidv4 } from 'uuid';

import { useUpdateGoal } from './use-update-goal';

export const useDuplicateGoalSection = () => {
  const { mutateAsync: duplicateTasks } = useDuplicateTasksMutation();
  const { submit: updateGoal } = useUpdateGoal();
  const relationships = useRelationships();

  return useCallback(
    async (sectionId: ID, goal: Goal) => {
      // get the section out of the goal
      const sectionIndex =
        goal.sections?.findIndex((section) => section.id === sectionId) ?? -1;

      const section = goal.sections?.[sectionIndex];
      if (!section) {
        return;
      }

      // get tasks of goal
      const mainTasks = await getTaskDocs({ goals: [goal.id] });

      // filter tasks to only those in the section
      const sectionTasks = section.tasks
        .map((taskId) => mainTasks.find(({ id }) => id === taskId))
        .filter(Boolean) as Task[];

      // get subtasks of all sectionTasks
      const subTaskIds = getSectionSubTaskIds(sectionTasks);
      const subTasks = await Promise.all(
        chunkArray(subTaskIds, 30).map(
          async (chunkedSubTaskIds) =>
            await getTaskDocs({ ids: chunkedSubTaskIds }),
        ),
      );

      // duplicate tasks
      const oldTasks = [...sectionTasks, ...subTasks.flat(1)];
      const newTasks = await duplicateTasks(oldTasks);

      const { newParentTasks, mainTaskPairs, newSection } =
        pairDuplicatedSectionTasks({
          section,
          sectionTasks,
          oldTasks,
          newTasks,
          newSectionId: uuidv4(),
        });

      // Tags and life-area membership live in separate relationship / life-area
      // documents keyed by task id, not on the task doc — copying the task docs
      // alone drops them, so re-point those references at the new tasks.
      const linkedUpdates = mainTaskPairs.length
        ? [
            updateLinkedRelationships({
              taskPairs: mainTaskPairs,
              relationships,
              updateRelationshipReferences,
            }),
            updateLinkedLifeArea({
              taskPairs: mainTaskPairs,
              getLifeAreaDocById,
              updateLifeArea,
            }),
          ]
        : [];

      // update the goal (new section & taskCount & completedTaskCount)
      updateGoal(
        buildSectionDuplicateUpdate(goal, {
          sectionId,
          newSection,
          taskCount: newParentTasks.length,
          completedTaskCount: newParentTasks.filter(
            (task) => !!task.completedAt,
          ).length,
        }),
      );

      await Promise.all(linkedUpdates);
    },
    [duplicateTasks, relationships, updateGoal],
  );
};
